import React, { useState } from "react";
import Image from "next/image";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";

import image1 from "/public/images/about-image-1.png";
import image2 from "/public/images/about-image-2.png";
import image3 from "/public/images/about-image-3.png";
import expertisIcon from "public/images/expertiseIcon.svg";
import facilityIcon from "public/images/facilityIcon.svg";
import securityIcon from "public/images/securityIcon.svg";
import ServiceBox from "../../components/ui_components/ServiceBox";
import YellowBox from "components/ui_components/YellowBox";
import ContactUsBtn from "components/ui_components/ContactUsBtn";
import JoinUsBtn from "components/ui_components/JoinUsBtn";
import SeoHead from "components/ui_components/SeoHead";
import { IS_PRODUCTION_ENVIRONMENT } from "helpers/constants";

const services = [
  {
    title: "local_expertise",
    logo: expertisIcon,
    description: "local_expertise_description",
    color: "#3F37C9",
  },
  {
    title: "ease_of_use",
    logo: facilityIcon,
    description: "ease_of_use_description",
    color: "#CA9C5E",
  },
  {
    title: "security_confidentiality",
    logo: securityIcon,
    description: "security_confidentiality_description",
    color: "#287F7A",
  },
];

function AboutPage({ isProduction }) {
  const { t: translate } = useTranslation("about");
  const [openIndex, setOpenIndex] = useState(0);

  const toggleSection = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  const sections = [
    {
      title: translate("access_database"),
      description: translate("vast_database_access_description"),
    },
    { title: translate("ease_search"), description: translate("ease_search_description") },
    {
      title: translate("improved_connectivity"),
      description: translate("improved_connectivity_description"),
    },
  ];

  return (
    <div className="bg-backgroundGrey p-4 lg:p-6 min-h-screen">
      <SeoHead
        title={translate("about_head_title")}
        description={translate("about_description")}
        isProduction={isProduction}
      />
      <main className="bg-white rounded-[21px] p-8 lg:px-24 grid gap-10 lg:gap-20 justify-center">
        <div className="grid gap-3 justify-center">
          <h1 className="text-center text-blue font-semibold text-2xl lg:text-5xl">
            {translate("about")}
          </h1>
          <p className="text-center text-black/50 font-normal text-base lg:text-lg leading-6 max-w-[1250px] mx-auto">
            {translate("welcome_message")}
          </p>
        </div>

        <div className="flex flex-col lg:justify-around gap-10 lg:flex-row-reverse">
          <div className=" w-full  lg:w-2/5 rounded-2xl ">
            <Image src={image1} alt="about image 1" objectFit="contain" className="rounded-2xl" />
          </div>
          <div className="flex flex-col  lg:gap-8 gap-4 lg:w-1/2">
            <YellowBox />
            <h2 className="ltr:text-left rtl:text-right text-blue font-semibold lg:font-bold text-2xl lg:text-5xl">
              {translate("build_better_future")}
            </h2>
            <p className="font-normal text-sm lg:text-base leading-9  text-darkGrey text-justify">
              {translate("understanding_importance")}
            </p>
            <ContactUsBtn />
          </div>
        </div>

        <div className="bg-[#23337708] rounded-2xl px-6 lg:px-12 py-8 grid gap-8 justify-center max-w-fit lg:mx-auto">
          <h3 className="font-bold text-center text-xl lg:text-4xl leading-7 text-blue max-w-[600px] lg:mx-auto">
            {translate("best_services")}
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {services.map((service) => (
              <ServiceBox
                key={service.title}
                title={service.title}
                description={service.description}
                logo={service.logo}
                color={service.color}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col lg:justify-around lg:flex-row gap-10 items-center">
          <div className=" w-full lg:w-2/5 ">
            <Image src={image2} alt="about image 2" objectFit="contain" />
          </div>
          <div className="flex flex-col gap-4 lg:gap-8 lg:w-1/2 ">
            <YellowBox />
            <h3 className="ltr:text-left rtl:text-right text-blue font-semibold lg:font-bold text-2xl lg:text-4xl leading-loose">
              {translate("what_we_offer")}
            </h3>
            {/* ------------- Access data base content --------- */}
            {sections.map((section, index) => (
              <div key={index} className="mb-4">
                {/* ----------- Title ------- */}
                <p
                  className="  flex items-center cursor-pointer gap-x-1"
                  onClick={() => toggleSection(index)}
                >
                  <span className="text-yellow font-bold text-xl lg:text-2xl leading-none ">
                    {openIndex === index ? "−" : "+"}
                  </span>
                  <p className="text-blue font-[600] text-base lg:text-xl">{section.title}</p>
                </p>

                {/*--------- Description --------- */}
                {openIndex === index && (
                  <p className="font-normal text-sm lg:text-lg text-darkGrey">
                    {section.description}
                  </p>
                )}
              </div>
            ))}
            <ContactUsBtn />
          </div>
        </div>
        <div className="flex flex-col lg:justify-around lg:flex-row-reverse  items-center gap-5 ">
          <div className="w-full lg:w-2/5  ">
            <Image src={image3} alt="about image 3" objectFit="contain" className="rounded-lg" />
          </div>
          <div className="flex flex-col gap-4 lg:w-1/2">
            <YellowBox />
            <h2 className="ltr:text-left rtl:text-right text-blue font-semibold lg:font-bold text-2xl lg:text-4xl leading-loose">
              {translate("our_mission")}
            </h2>
            <p className="font-normal text-sm lg:text-lg text-darkGrey text-justify">
              {translate("our_mission_description")}
            </p>
            <h4 className="text-justify text-blue font-semibold lg:font-bold text-lg lg:text-1xl ">
              {translate("join_us_today")}
            </h4>
            <JoinUsBtn />
          </div>
        </div>
      </main>
    </div>
  );
}

export default AboutPage;

export async function getServerSideProps({ locale }) {
  return {
    props: {
      isProduction: IS_PRODUCTION_ENVIRONMENT,
      ...(await serverSideTranslations(locale, [
        "layout",
        "about",
        "categories",
        "signup_promotion",
      ])),
    },
  };
}
